import { useCallback, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { prefersReducedMotion } from "../../landing/motion"
import { ABOUT_HERO, ABOUT_STEPS, ABOUT_TOUR } from "../aboutWorkspace"
import { Scene } from "./Mocks"
import { useInView } from "./useReveal"

/** How long each step stays, and how often the progress bar and clock tick. */
export const STEP_MS = 4000
const TICK = 50
const LAST = ABOUT_STEPS.length - 1

const Icon = ({ d }: { d: string }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
)

/**
 * The walkthrough: one stage, one scene that moves from step to step. Auto-plays once in view, pauses on
 * hover, keyboard focus and a hidden tab, loops from the last step back to the first, and stops for good
 * once the visitor steps through it themselves (or presses pause); play restarts it. With reduced motion
 * it never auto-plays and swaps steps at once.
 */
export function Tour() {
  const [reduced] = useState(() => prefersReducedMotion())
  const [step, setStep] = useState(0)
  const [playing, setPlaying] = useState(!reduced)
  const [hover, setHover] = useState(false)
  const [focus, setFocus] = useState(false)
  const [tabHidden, setTabHidden] = useState(false)
  const [live, setLive] = useState(reduced)
  const [announce, setAnnounce] = useState("")
  const [stageRef, inView] = useInView<HTMLDivElement>()
  const railRef = useRef<HTMLOListElement>(null)
  const elapsed = useRef(0)
  const pointer = useRef(false)
  const swipe = useRef<number | null>(null)

  useEffect(() => { if (inView) setLive(true) }, [inView])
  useEffect(() => {
    const on = () => setTabHidden(document.visibilityState === "hidden")
    document.addEventListener("visibilitychange", on)
    return () => document.removeEventListener("visibilitychange", on)
  }, [])

  const setProgress = (v: number) => railRef.current?.style.setProperty("--p", String(v))

  const goTo = useCallback((i: number, byUser = true) => {
    const n = (i + ABOUT_STEPS.length) % ABOUT_STEPS.length
    elapsed.current = 0
    setProgress(0)
    setStep(n)
    if (byUser) setPlaying(false)
    if (byUser) setAnnounce(`Step ${n + 1} of ${ABOUT_STEPS.length}: ${ABOUT_STEPS[n].title}. ${ABOUT_STEPS[n].text}`)
  }, [])

  const running = playing && inView && !hover && !focus && !tabHidden && !reduced
  useEffect(() => {
    if (!running) return
    const id = setInterval(() => {
      elapsed.current += TICK
      if (elapsed.current < STEP_MS) { setProgress(elapsed.current / STEP_MS); return }
      goTo(step < LAST ? step + 1 : 0, false)
    }, TICK)
    return () => clearInterval(id)
  }, [running, step, goTo])

  const togglePlay = () => {
    if (playing) { setPlaying(false); return }
    setPlaying(true)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    pointer.current = false
    if (e.key === "ArrowRight") { e.preventDefault(); goTo(step + 1) }
    else if (e.key === "ArrowLeft") { e.preventDefault(); goTo(step - 1) }
  }
  const onFocus = () => { if (!pointer.current) setFocus(true) }
  const onBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocus(false)
  }
  const onPointerUp = (e: React.PointerEvent) => {
    const from = swipe.current
    swipe.current = null
    if (from === null) return
    const dx = e.clientX - from
    if (Math.abs(dx) > 40) goTo(step + (dx < 0 ? 1 : -1))
  }

  const state = (i: number) => (i < step ? "done" : i === step ? "on" : "next")
  const label = playing ? "Pause the walkthrough" : "Play the walkthrough"

  return (
    <section
      className="wa-tour"
      aria-roledescription="carousel"
      aria-label={ABOUT_TOUR.label}
      data-playing={playing && !reduced ? "true" : "false"}
      onKeyDown={onKeyDown}
    >
      <div
        ref={stageRef}
        className="wa-stage"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onPointerDown={() => { pointer.current = true }}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        <ol ref={railRef} className="wa-rail">
          {ABOUT_STEPS.map((s, i) => (
            <li key={s.n} data-state={state(i)}>
              <button type="button" className="wa-rail-b" aria-current={i === step ? "step" : undefined} onClick={() => goTo(i)}>
                <span className="wa-track" aria-hidden="true"><span className="wa-fill" /></span>
                <span className="wa-rail-t"><span className="nl-n">{s.n}</span><span className="wa-rail-l">{s.label}</span></span>
              </button>
            </li>
          ))}
        </ol>

        <div className="wa-view" onPointerDown={(e) => { swipe.current = e.clientX }} onPointerUp={onPointerUp} onPointerCancel={() => { swipe.current = null }}>
          <Scene s={live ? step + 1 : 0} />
        </div>

        <div className="wa-capbar">
          <div className="nl-steps wa-caps">
            {ABOUT_STEPS.map((s, i) => (
              <div key={s.n} className="wa-cap" role="group" aria-roledescription="slide" aria-label={`Step ${i + 1} of ${ABOUT_STEPS.length}`} aria-hidden={i === step ? undefined : true} data-on={i === step ? "true" : "false"}>
                <span className="nl-n">{s.n}</span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
                <span className="nl-vh">{s.alt}</span>
                {i === LAST ? <Link className="nr nr-btn wa-cap-cta" data-variant="primary" to={ABOUT_HERO.primary.to} tabIndex={i === step ? undefined : -1}>{ABOUT_HERO.primary.label}</Link> : null}
              </div>
            ))}
          </div>
          <div className="wa-ctl">
            <button type="button" className="nr nr-btn wa-ib" aria-label="Previous step" onClick={() => goTo(step - 1)}><Icon d="M10 3 5 8l5 5" /></button>
            {reduced ? null : (
              <button type="button" className="nr nr-btn wa-ib" aria-label={label} onClick={togglePlay}>
                {playing ? <Icon d="M5.5 3.5v9M10.5 3.5v9" /> : <Icon d="M5 3.2v9.6L12.5 8z" />}
              </button>
            )}
            <button type="button" className="nr nr-btn wa-ib" aria-label="Next step" onClick={() => goTo(step + 1)}><Icon d="m6 3 5 5-5 5" /></button>
            <span className="nl-n wa-cnt" aria-hidden="true">{String(step + 1).padStart(2, "0")} / {String(ABOUT_STEPS.length).padStart(2, "0")}</span>
          </div>
        </div>
        <div className="nl-vh" aria-live="polite" aria-atomic="true">{announce}</div>
      </div>
    </section>
  )
}
